import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Gray600, Primary500 } from '@mehilainen/mds-customer/colors'
import { Clock } from '@mehilainen/mds-customer/icons'
import { spacing } from '@mehilainen/mds-tokens'
import { Dayjs } from 'dayjs'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { formatCallbackTime, formatDate3 } from '../../utils/text'
import { Text } from '../Typography/Typography'

const TimeDetailsContainer = styled.div<{ variant: 'S' | 'M'; isMobile: boolean }>`
  display: flex;
  gap: ${(props) => (props.variant === 'S' ? spacing[200].rem : spacing[300].rem)};

  ${(props) =>
    props.isMobile
      ? css`
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        `
      : css`
          flex-direction: column;
          align-self: center;
          min-width: 80px;
          margin-left: ${props.variant === 'M' ? spacing[300].rem : 0};
        `}
`

const DateAndTimeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[200].rem};
`

const DurationWithIcon = styled(Text)`
  display: flex;
  align-items: center;

  svg {
    color: ${Gray600};
    margin-right: ${spacing[250].rem};
  }
`

interface Props {
  date: Dayjs
  duration: number | null
  isMobile: boolean
  variant?: 'S' | 'M'
  isCallbackAppointment?: boolean
  className?: string
}

const AppointmentTimeDetails: React.FC<Props> = ({
  date,
  duration,
  isMobile,
  variant = 'M',
  isCallbackAppointment = false,
  className,
}) => {
  const { t } = useTranslation()

  const formattedDate = formatDate3(date, t)
  const formattedTime = date.format('H:mm')
  const displayTime = isCallbackAppointment ? formatCallbackTime(date) : formattedTime

  const showDuration = !isCallbackAppointment && duration !== null

  return (
    <TimeDetailsContainer
      variant={variant}
      isMobile={isMobile}
      className={className}
      data-cy="appointmentTileTime"
    >
      {isMobile ? (
        <>
          <DateAndTimeContainer>
            <Text $size={300} $weight="Medium" $height="Medium" $color={Primary500} $capitalize>
              {formattedDate}
            </Text>
            <Text
              $size={isMobile ? 300 : 500}
              $height="Medium"
              $color={Primary500}
              $weight="Medium"
            >
              {displayTime}
            </Text>
          </DateAndTimeContainer>
          {showDuration && (
            <DurationWithIcon
              $size={300}
              $height="Medium"
              $color={Gray600}
              $weight="Medium"
              data-cy={`appointmentTileDetails-${duration}`}
            >
              <span>{`${Math.round(duration!)} min`}</span>
            </DurationWithIcon>
          )}
        </>
      ) : (
        <>
          <Text
            $size={isMobile ? 200 : 300}
            $weight="Regular"
            $height="Medium"
            $color={Primary500}
            $capitalize
          >
            {formattedDate}
          </Text>
          <Text
            $size={isMobile ? (isCallbackAppointment ? 300 : 450) : 500}
            $weight="Medium"
            $height={isCallbackAppointment ? 'Medium' : 'Small'}
            $color={Primary500}
          >
            {displayTime}
          </Text>
          {showDuration && (
            <DurationWithIcon
              $size={300}
              $height="Medium"
              $color={Gray600}
              data-cy={`appointmentTileDetails-${duration}`}
            >
              {variant === 'M' && <Clock />}
              <span>{`${Math.round(duration)} min`}</span>
            </DurationWithIcon>
          )}
        </>
      )}
    </TimeDetailsContainer>
  )
}

export default styled(AppointmentTimeDetails)``
